import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, Users, BookOpen, Award, ArrowRight, FlaskConical, GraduationCap, ShieldCheck, Star } from 'lucide-react';

const HERO_IMG = "https://images.pexels.com/photos/3985149/pexels-photo-3985149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const MENTOR_IMG = "https://images.unsplash.com/photo-1645066928295-2506defde470?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxtZWRpY2FsJTIwZG9jdG9yJTIwcHJvZmVzc29yJTIwcG9ydHJhaXQlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzcxNTQzMDM0fDA&ixlib=rb-4.1.0&q=85";
const LAB_IMG = "https://images.pexels.com/photos/8442369/pexels-photo-8442369.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="page-container" data-testid="landing-page">
      {/* Hero */}
      <section className="relative hero-gradient noise-overlay overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="animate-slide-left">
              <Badge className="mb-6 bg-[#1a5c5c]/10 text-[#1a5c5c] border-[#1a5c5c]/20 hover:bg-[#1a5c5c]/15 px-3 py-1">
                <FlaskConical className="h-3.5 w-3.5 mr-1.5" /> Medical Research Platform
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-extrabold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Where Future Doctors Master <span className="text-[#1a5c5c]">Research</span>, <span className="text-[#d4af37]">Innovation</span> & Clinical Excellence
              </h1>
              <p className="text-lg leading-relaxed text-slate-600 mb-8 max-w-xl">
                Connect with world-class research mentors, access structured research opportunities, and accelerate your medical research career with PRASHA CoRe.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => navigate('/auth?tab=signup')}
                  className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white px-6 py-2.5 text-base btn-teal-glow active:scale-95 transition-transform"
                  data-testid="hero-get-started-btn"
                >
                  Get Started Free <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/research')}
                  className="border-[#1a5c5c]/20 text-[#1a5c5c] hover:bg-[#1a5c5c]/5 px-6 py-2.5 text-base active:scale-95 transition-transform"
                  data-testid="hero-explore-btn"
                >
                  Explore Research
                </Button>
              </div>
            </div>
            <div className="animate-slide-right hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-[#1a5c5c]/5 rounded-2xl -rotate-3" />
                <img src={HERO_IMG} alt="Medical students collaborating" className="relative rounded-xl shadow-2xl object-cover w-full h-[420px]" />
                <div className="absolute -bottom-4 -left-4 glass-card rounded-xl p-4 shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Award className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">500+ Research Projects</p>
                      <p className="text-xs text-slate-500">Across 18 specialties</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1a5c5c] py-10" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {/* {[
              { num: '500+', label: 'Research Projects' },
              { num: '200+', label: 'Expert Mentors' },
              { num: '10,000+', label: 'Students' },
              { num: '50+', label: 'Universities' },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{stat.num}</p>
                <p className="text-sm text-white/70 mt-1">{stat.label}</p>
              </div>
            ))} */}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>
              Everything You Need to Excel in Medical Research
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              From discovering opportunities to publishing your findings, PRASHA CoRe supports every step of your research journey.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: <Search className="h-6 w-6" />,
                title: 'Research Opportunities',
                desc: 'Browse hundreds of curated research projects across 18+ medical specialties. Filter by duration, mode, and compensation.',
                img: LAB_IMG,
              },
              {
                icon: <Users className="h-6 w-6" />,
                title: 'Expert Mentorship',
                desc: 'Connect with verified research mentors from top institutions worldwide. Get guidance from experienced professionals.',
                img: MENTOR_IMG,
              },
              {
                icon: <BookOpen className="h-6 w-6" />,
                title: 'Medical Courses',
                desc: 'Enroll in courses spanning the entire medical ecosystem — AI in healthcare, public health, pharmacology, ethics, digital health, and more.',
                img: "https://images.pexels.com/photos/9574341/pexels-photo-9574341.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
              },
            ].map((feature, i) => (
              <div key={feature.title} className={`group bg-white border border-slate-100 rounded-xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 animate-fade-in-up animate-delay-${(i + 1) * 100}`}>
                <div className="h-48 overflow-hidden">
                  <img src={feature.img} alt={feature.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <div className="h-10 w-10 rounded-lg bg-[#1a5c5c]/10 flex items-center justify-center text-[#1a5c5c] mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>{feature.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>
              How PRASHA CoRe Works
            </h2>
            <p className="text-base md:text-lg text-slate-600">Three simple steps to start your research journey.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', icon: <GraduationCap className="h-7 w-7" />, title: 'Create Your Profile', desc: 'Sign up, add your university details, research interests, and skills to get personalized recommendations.' },
              { step: '02', icon: <Search className="h-7 w-7" />, title: 'Discover & Apply', desc: 'Browse research projects, filter by specialty, and submit applications to opportunities that match your goals.' },
              { step: '03', icon: <FlaskConical className="h-7 w-7" />, title: 'Research & Grow', desc: 'Work with your mentor, learn new skills, publish findings, and earn certificates to advance your career.' },
            ].map((item, i) => (
              <div key={item.step} className="relative pl-6 border-l-2 border-[#1a5c5c]/20">
                <span className="absolute -left-3 top-0 h-6 w-6 rounded-full bg-[#1a5c5c] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <div className="h-12 w-12 rounded-lg bg-[#1a5c5c]/10 flex items-center justify-center text-[#1a5c5c] mb-4">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-12 text-center" style={{ fontFamily: 'Manrope' }}>
            Trusted by Medical Students & Researchers
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: 'Dr. Sneha Verma', role: 'Resident, AIIMS Delhi', text: 'PRASHA CoRe helped me find the perfect cardiology research project. The mentorship I received was invaluable for my career.', rating: 5 },
              { name: 'Arjun Mehta', role: 'Medical Student, MAMC', text: 'The structured courses on research methodology gave me the confidence to start my first research paper. Highly recommend!', rating: 5 },
              { name: 'Dr. Kavita Sharma', role: 'Professor, Stanford', text: 'As a mentor, PRASHA CoRe makes it incredibly easy to find motivated students and manage research projects efficiently.', rating: 5 },
            ].map((t) => (
              <div key={t.name} className="bg-white border border-slate-100 rounded-xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-shadow duration-300">
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-[#d4af37] text-[#d4af37]" />
                  ))}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-4 italic">"{t.text}"</p>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[#1a5c5c] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3" />
        </div>
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
            Ready to Start Your Research Journey?
          </h2>
          <p className="text-white/80 text-base md:text-lg mb-8">
            Join thousands of medical students who are building their research careers with PRASHA CoRe.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              onClick={() => navigate('/auth?tab=signup')}
              className="bg-white text-[#1a5c5c] hover:bg-slate-100 px-6 py-2.5 text-base font-semibold active:scale-95 transition-transform"
              data-testid="cta-signup-btn"
            >
              Create Free Account <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/research')}
              className="border-white/30 text-white hover:bg-white/10 px-6 py-2.5 text-base active:scale-95 transition-transform"
              data-testid="cta-explore-btn"
            >
              Browse Research
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
